<script lang="ts" setup>
import { computed } from 'vue'
import { formatBytes } from '@/utils'

const props = defineProps<{
  usedBytes: number
  totalBytes: number
  showLabel?: boolean
  compact?: boolean
}>()

const percentage = computed(() => {
  if (!props.totalBytes) return 0
  return Math.min(100, (props.usedBytes / props.totalBytes) * 100)
})

const statusColor = computed(() => {
  if (percentage.value >= 90) return 'error'
  if (percentage.value >= 75) return 'warning'
  return 'success'
})

const usedFormatted = computed(() => formatBytes(props.usedBytes))
const totalFormatted = computed(() => formatBytes(props.totalBytes))
</script>

<template>
  <div class="quota-progress" :class="{ compact }">
    <div v-if="showLabel" class="quota-label">
      <span class="used">{{ usedFormatted }}</span>
      <span class="separator">/</span>
      <span class="total">{{ totalFormatted }}</span>
    </div>
    <div class="progress-container">
      <div class="progress-track">
        <div
          class="progress-fill"
          :class="statusColor"
          :style="{ width: `${percentage}%` }"
        />
      </div>
      <span v-if="compact" class="percentage-label">{{ usedFormatted }}</span>
    </div>
    <div v-if="!compact && !showLabel" class="quota-info">
      <span>{{ usedFormatted }} {{ $t('dashboard.mail.quota.used') }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.quota-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.compact {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.quota-label {
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: 0.8rem;
}

.used {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.separator {
  color: rgba(200, 218, 255, 0.4);
}

.total {
  color: rgba(200, 218, 255, 0.6);
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.progress-track {
  flex: 1;
  height: 6px;
  background: rgba(15, 23, 48, 0.6);
  border-radius: 3px;
  overflow: hidden;

  .compact & {
    height: 4px;
    min-width: 60px;
  }
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;

  &.success {
    background: linear-gradient(90deg, #66bb6a, #81c784);
  }

  &.warning {
    background: linear-gradient(90deg, #ffa726, #ffb74d);
  }

  &.error {
    background: linear-gradient(90deg, #ef5350, #f44336);
  }
}

.percentage-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  min-width: 32px;
}

.quota-info {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Light Theme */
.v-theme--lightTheme {
  .used {
    color: rgba(15, 23, 42, 0.95);
  }

  .separator {
    color: rgba(15, 23, 42, 0.4);
  }

  .total {
    color: rgba(15, 23, 42, 0.6);
  }

  .progress-track {
    background: rgba(15, 23, 42, 0.1);
  }

  .percentage-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .quota-info {
    color: rgba(15, 23, 42, 0.5);
  }
}
</style>
